import Link from '@components/common/Link/Link'
import Button from '@components/ui/ButtonV2/Button'
import InputV2 from '@components/ui/InputV2/InputV2'
import { useCustomerPasswordResetToken } from '@features/account/api/customer-password-reset-token'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import { FC, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { ImSpinner2 } from 'react-icons/im'
import { z } from 'zod'
import s from './M58ForgotPasswordForm.module.scss'

type Step1FormData = {
  email: string
}

type Step1Props = {
  email?: string
  onNext: (data: { email: string }) => void
}

const baseSchema = z.object({
  email: z.string().min(1, 'Required').email(),
})

const PasswordSendCodeStep: FC<Step1Props> = ({ email, onNext, ...props }) => {
  const methods = useForm({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      email: email ?? '',
    },
  })

  const { mutateAsync, isPending } = useCustomerPasswordResetToken()
  const {
    formState: { errors },
  } = methods

  const emailField = methods.watch('email')

  const { addError, loading, customErrors, clearErrors } = useCustomErrors()

  const forgotPasswordAction: SubmitHandler<Step1FormData> = async (data) => {
    const request = await mutateAsync({
      email: data.email ?? '',
    })

    if (!request.customerPasswordResetToken.errors) {
      await onNext(data)
      clearErrors()
    } else {
      addError({
        code: 'unkown',
        message: 'Something went wrong',
      })
    }
  }

  // on valid form, clear errors
  useEffect(() => {
    if (methods.formState.isValid) {
      methods.clearErrors()
    }
  }, [methods.formState.isValid])

  return (
    <FormProvider {...methods}>
      <form
        className={s.form}
        onSubmit={methods.handleSubmit(forgotPasswordAction)}
      >
        {customErrors && customErrors.length > 0 && (
          <div className={s.errorContainer}>
            {customErrors.map((error) => (
              <div key={error.code} className={s.error}>
                {error.message}
              </div>
            ))}
          </div>
        )}
        <InputV2
          id="email"
          type="email"
          placeholder="Email"
          value={methods.getValues('email')}
          required
          errorMessage={errors.email?.message as string}
        />

        <div className={s.submitWrapper}>
          <Button
            className={s.button}
            variant="secondary"
            type="submit"
            disabled={!methods.formState.isValid || isPending || loading}
          >
            {isPending || loading ? (
              <ImSpinner2 className={s['icon-spin']} />
            ) : (
              'Submit'
            )}
          </Button>
          <span className={s.secondaryAction}>
            or{' '}
            <Link
              link={{ url: '/account/login' }}
              query={emailField ? { email: emailField } : undefined}
            >
              login instead
            </Link>
          </span>
        </div>
      </form>
    </FormProvider>
  )
}

export default PasswordSendCodeStep
